import { Box, Button, Typography, useTheme } from '@mui/material';
import Divider from 'components/divider';
import ListCell from 'components/list/list-cell';
import NewListRow from 'components/new-list-row';
import Tooltip from 'components/tooltip';
import { useAccountState } from 'contexts/account/account-context';
import dayjs from 'dayjs';
import { AlertTriangle } from 'lucide-react';
import React, { useMemo } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { saveState } from 'utils/storage';
import { useOrderFeedState } from 'views/order-feed/context/order-feed-context';
import { useMySupplyPlanState } from 'views/order-feed/features/my-supply-plan/context/my-supply-plan-context';
import DistributionDetailsDialog from 'views/order-feed/features/my-supply-plan/features/ofd-1.0/components/order-list/order-distribution-details-dialog';
import EditOrderDialog, {
  EditOrderDialogOrder,
} from 'views/order-feed/features/my-supply-plan/features/ofd-1.0/components/order/edit-order-dialog';
import OrderProductList from 'views/order-feed/features/my-supply-plan/features/ofd-1.0/components/order/order-product-list';
import { OrderListItem } from 'views/order-feed/features/my-supply-plan/features/ofd-1.0/types/order-list-item.type';
import { useGoogleTagManager } from 'views/order-feed/features/my-supply-plan/hooks/use-google-tag-manager';
import { CreateOrderFormValues } from 'views/order-feed/features/my-supply-plan/types/create-order-form-values.type';
import { OrderType, SupplyPlanType } from 'views/order-feed/features/my-supply-plan/types/google-tag-manager/order-feed-events.type';
import { MySupplyPlanParams } from 'views/order-feed/features/my-supply-plan/types/my-supply-plan-params.type';
import { REPEAT_ORDER_ANCHOR_ID } from 'views/order-feed/features/my-supply-plan/utils/create-order-utils';
import {
  getDefaultDispatchDate,
  getOrderDateValueFromParts,
  getSiteTransportCompanies,
  isOrderDateInShippingSchedule,
} from 'views/order-feed/features/my-supply-plan/utils/order-utils';

type Props = {
  order: OrderListItem;
};

const toEditOrderDialogOrder = (order: OrderListItem): EditOrderDialogOrder => ({
  customerErpId: order.customerErpId,
  orderNumber: order.orderNumber,
  transportMethod: order.transportMethod,
  requestedDispatchDate: getOrderDateValueFromParts(order.plantId, order.vessel, order.dispatchDate || order.requestedDispatchDate),
  siteErpId: order.siteErpId,
  orderLines: order.orderLines.map((line) => ({
    itemNumber: line.itemNumber,
    orderQuantity: line.orderQuantity,
  })),
  orderComment: order.orderComment || '',
  customerOrderReference: order.customerOrderReference || '',
  plantId: order.plantId,
  vessel: order.vessel,
  totalOrderQuantity: order.totalOrderQuantity,
});

const getArrivalDate = (order: OrderListItem): string | undefined => {
  const candidates = [order.arrivalTime, order.currentEta, order.estimatedArrivalDate];

  return candidates.find((date) => !!date && dayjs(date).isValid());
};

type OrderBodyProps = {
  order: OrderListItem;
};

const StandardOrderBodyContent: React.FC<OrderBodyProps> = ({ order }) => {
  const theme = useTheme();

  return (
    <Box display="flex" flexDirection="column" sx={{ border: `1px solid ${theme.palette.darkBlue.o25}`, borderRadius: 1 }}>
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Box
          display="flex"
          flexDirection="column"
          gap={4}
          padding={4}
          width={{ xs: '100%', md: '35%' }}
          sx={{ borderBottom: { xs: `1px solid ${theme.palette.darkBlue.o25}`, md: 'none' } }}>
          <Typography variant="h6" fontWeight="bold">
            <FormattedMessage id="orderFeed.ofdMySupplyPlan.orders.list.orderDetails" />
          </Typography>
          <Box display="flex" gap={2}>
            <Typography fontWeight="bold">
              <FormattedMessage id="orderFeed.ofdMySupplyPlan.orders.list.vesselName" />:
            </Typography>
            <Typography textAlign="right">{order.vessel || <FormattedMessage id="general.notApplicable" />}</Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Typography fontWeight="bold">
              <FormattedMessage id="orderFeed.ofdMySupplyPlan.orders.list.plantName" />:
            </Typography>
            <Typography textAlign="right">{order.plantName || <FormattedMessage id="general.notApplicable" />}</Typography>
          </Box>
        </Box>
        <Divider flexItem orientation="vertical" dark variant="middle" />
        <Box display="flex" flexDirection="column" width="100%" padding={4}>
          <OrderProductList orderLines={order.orderLines} />
          {!!order.orderDistributionDetails?.siloDistributions.length && (
            <Box display="flex" justifyContent="flex-end">
              <DistributionDetailsDialog distributionDetails={order.orderDistributionDetails} />
            </Box>
          )}
        </Box>
      </Box>
      <Divider flexItem dark variant="middle" />
      <Box display="flex" flexDirection="column" gap={1} padding={4}>
        <Box display="flex" gap={2}>
          <Typography fontWeight="bold">
            <FormattedMessage id="orderFeed.ofdMySupplyPlan.orders.customerReference" />:
          </Typography>
          <Typography textAlign="right">{order.customerOrderReference || <FormattedMessage id="general.notApplicable" />}</Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Typography fontWeight="bold">
            <FormattedMessage id="orderFeed.ofdMySupplyPlan.orders.orderComment" />:
          </Typography>
          <Typography textAlign="right">{order.orderComment || <FormattedMessage id="general.notApplicable" />}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

const StandardOrderRowBody: React.FC<OrderBodyProps> = ({ order }) => {
  const { account } = useAccountState();
  const params = useParams<MySupplyPlanParams>();
  const {
    dispatchDatesQuery: { data: dispatchSchedule },
  } = useOrderFeedState();
  const {
    repeatOrder: { request: requestRepeatOrder },
  } = useMySupplyPlanState();
  const defaultDispatchDate = getDefaultDispatchDate(dispatchSchedule);

  const handleSaveOrderData = (data: CreateOrderFormValues) => {
    saveState({ key: 'createOrderData', state: data });
    requestRepeatOrder(data);
    document.getElementById(REPEAT_ORDER_ANCHOR_ID)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} paddingBottom={2}>
      <StandardOrderBodyContent order={order} />
      <Box display="flex" justifyContent="flex-end">
        <Button
          size="small"
          onClick={() => {
            const deliveryMethods = getSiteTransportCompanies(account.company?.addressAccess, params.globalSiteId);
            const deliveryMethod = deliveryMethods?.length === 1 ? deliveryMethods[0] : undefined;
            const { key: transportMethod = '', name: addressId = '' } = deliveryMethod ?? {};
            handleSaveOrderData({
              transportMethod,
              addressId,
              customerOrderReference: '',
              orderComment: '',
              requestedDispatchDate: defaultDispatchDate,
              siteErpId: order.siteErpId,
              orderLines: order.orderLines.map((line) => ({ itemNumber: line.itemNumber, orderQuantity: line.orderQuantity })),
            });
          }}>
          <FormattedMessage id="general.repeatOrder" />
        </Button>
      </Box>
    </Box>
  );
};

const StandardOrderRow: React.FC<Props> = ({ order }) => {
  const intl = useIntl();
  const theme = useTheme();
  const { account } = useAccountState();
  const {
    dispatchDatesQuery: { data: dispatchSchedule },
  } = useOrderFeedState();
  const { createOrderFeedEvent } = useGoogleTagManager();

  const isInShippingSchedule = () =>
    isOrderDateInShippingSchedule(
      dispatchSchedule,
      getOrderDateValueFromParts(order.plantId, order.vessel, order.dispatchDate || order.requestedDispatchDate)
    );

  const canEditOrder = () => {
    if (!account.company?.features?.hasOrderFeed || order.remainingEditDays <= 0 || !order.lastPossibleEditDate) return false;

    return dayjs().isSameOrBefore(dayjs(order.lastPossibleEditDate), 'day');
  };

  const arrivalDate = useMemo(() => getArrivalDate(order), [order]);
  const orderOutsideSchedule = !isInShippingSchedule();
  const editable = canEditOrder();

  return (
    <NewListRow
      colSpan={8}
      body={<StandardOrderRowBody order={order} />}
      bodyOnClick={() => {
        createOrderFeedEvent('dropdown', {
          orderType: OrderType.CONFIRMED,
          orderNumber: order.orderNumber,
          supplyPlanType: SupplyPlanType.OFD1,
        });
      }}>
      <ListCell align="center">
        {orderOutsideSchedule && (
          <Tooltip arrow title={<FormattedMessage id="orderFeed.alert.shippingSchedule" />}>
            <AlertTriangle
              fill={theme.palette.warning.light}
              color={theme.palette.darkBlue.main}
              size={18}
              style={{ display: 'flex' }}
            />
          </Tooltip>
        )}
      </ListCell>
      <ListCell>{`#${order.orderNumber}`}</ListCell>
      <ListCell>
        {!order.remainingEditDays
          ? intl.formatMessage({ id: 'orderFeed.listOrderSupplyPlan.orderList.noChangeableLabel' })
          : order.remainingEditDays === 1
            ? `${order.remainingEditDays} ${intl.formatMessage({ id: 'orderFeed.listOrderSupplyPlan.orderList.remainingDayLabel' })}`
            : `${order.remainingEditDays} ${intl.formatMessage({ id: 'orderFeed.listOrderSupplyPlan.orderList.remainingDaysLabel' })}`}
      </ListCell>
      <ListCell>
        <FormattedDate value={order.dispatchDate} day="2-digit" month="short" year="numeric" />
      </ListCell>
      <ListCell>{arrivalDate ? <FormattedDate value={arrivalDate} day="2-digit" month="short" year="numeric" /> : '-'}</ListCell>
      <ListCell align="right">
        <FormattedNumber value={order.totalOrderQuantity} style="unit" unit="kilogram" />
      </ListCell>
      <ListCell align="right" sx={{ width: { xs: '100%', md: 'auto' } }}>
        <Box display="flex" justifyContent={{ xs: 'space-between', md: 'flex-end' }} alignItems="center" sx={{ width: '100%' }}>
          {editable ? <EditOrderDialog order={toEditOrderDialogOrder(order)} /> : null}
        </Box>
      </ListCell>
    </NewListRow>
  );
};

export default React.memo(StandardOrderRow);
